import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowTopRightOnSquareIcon,
  ArrowPathIcon,
  InformationCircleIcon,
  LinkIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClipboardDocumentIcon,
  CodeBracketIcon,
} from '@heroicons/react/24/outline';
import { api } from '../services/api';
import toast from '../utils/toast';
import SearchableSelect from '../components/SearchableSelect';

const ConfigureForwardUrlPage = () => {
  const [clients, setClients] = useState({});
  const [selectedClientId, setSelectedClientId] = useState('');
  const [forwardUrl, setForwardUrl] = useState('');
  const [originalUrl, setOriginalUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingUrl, setLoadingUrl] = useState(false);
  const [showPayloadPreview, setShowPayloadPreview] = useState(false);

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.getWhatsAppClients();
      setClients(res.data?.clients || {});
    } catch (e) {
      console.error('Error fetching WhatsApp clients:', e);
      toast.error('Failed to load connections');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  useEffect(() => {
    if (!selectedClientId) {
      setForwardUrl('');
      setOriginalUrl('');
      return;
    }
    let cancelled = false;
    setLoadingUrl(true);
    api
      .getClientStatus(selectedClientId)
      .then((res) => {
        if (!cancelled) {
          const url = res.data?.message_forward_url;
          const urlValue = typeof url === 'string' ? url : '';
          setForwardUrl(urlValue);
          setOriginalUrl(urlValue);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          console.error('Error fetching client status:', e);
          toast.error('Failed to load forward URL for this connection');
          setForwardUrl('');
          setOriginalUrl('');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingUrl(false);
      });
    return () => { cancelled = true; };
  }, [selectedClientId]);

  const clientOptions = Object.values(clients).map((c) => ({
    value: c.client_id,
    label: c.agent_name || c.display_name || `Client ${c.client_id}`,
    description: c.whatsapp_number || c.client_id,
    icon: '📱',
  }));

  const selectedClient = selectedClientId 
    ? Object.values(clients).find(c => c.client_id === selectedClientId) 
    : null;

  const hasChanges = forwardUrl.trim() !== originalUrl;
  const isForwardingEnabled = !!originalUrl;

  const handleSave = async () => {
    if (!selectedClientId) {
      toast.error('Select a connection first');
      return;
    }
    const url = forwardUrl.trim();
    if (!url) {
      toast.error('Enter a forward URL or use Clear URL to disable forwarding.');
      return;
    }
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      toast.error('URL must start with http:// or https://');
      return;
    }
    if (url.length > 500) {
      toast.error('URL must be at most 500 characters');
      return;
    }
    setSaving(true);
    try {
      await api.updateClient(selectedClientId, { message_forward_url: url });
      setOriginalUrl(url);
      toast.success('Forward URL saved successfully!');
    } catch (e) {
      console.error('Error saving forward URL:', e);
      const msg = e.response?.data?.error || e.message || 'Failed to save forward URL';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!selectedClientId) return;
    setSaving(true);
    try {
      await api.updateClient(selectedClientId, { message_forward_url: '' });
      setForwardUrl('');
      setOriginalUrl('');
      toast.success('Message forwarding disabled');
    } catch (e) {
      console.error('Error clearing forward URL:', e);
      const msg = e.response?.data?.error || e.message || 'Failed to disable forwarding';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const samplePayload = {
    event: "message.received",
    timestamp: new Date().toISOString(),
    client_id: selectedClientId || "your_client_id",
    message: {
      id: "ABCD1234567890",
      chat_id: "233554694531@c.us",
      from: "233554694531",
      from_name: "John Doe",
      text: "Hello, I need help with my order",
      is_group: false,
      group_name: null,
      timestamp: new Date().toISOString()
    },
    response: {
      will_respond: true,
      reason: "private_message"
    }
  };

  const copyPayloadToClipboard = () => {
    navigator.clipboard.writeText(JSON.stringify(samplePayload, null, 2));
    toast.success('Sample payload copied to clipboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your connections...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <ArrowTopRightOnSquareIcon className="h-8 w-8 text-green-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-semibold text-gray-900">Message Forwarding</h1>
                  <p className="text-sm text-gray-600">Forward incoming WhatsApp messages to your external API</p>
                </div>
              </div>
              <button
                onClick={fetchClients}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center">
              <LinkIcon className="h-5 w-5 text-gray-400 mr-2" />
              <h2 className="text-lg font-medium text-gray-900">Configure Forward URL</h2>
            </div>
          </div>

          <div className="px-6 py-6 space-y-6">

            {/* Connection Selection */}
            <div className="space-y-4">
              <h3 className="text-base font-medium text-gray-900 border-b border-gray-200 pb-2">
                Select Connection
              </h3>

              <SearchableSelect
                label="WhatsApp Connection"
                name="connection"
                options={clientOptions}
                value={selectedClientId}
                onChange={(e) => setSelectedClientId(e.target?.value ?? '')}
                placeholder="Search and select a connection..."
                isRequired
                isSearchable
                isClearable
              />

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex">
                  <InformationCircleIcon className="h-5 w-5 text-blue-400 flex-shrink-0" />
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-blue-800">Connection Selection</h4>
                    <p className="mt-1 text-sm text-blue-700">
                      Choose a WhatsApp connection to configure message forwarding. Each connection can have its own forward URL.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {selectedClientId && (
              <>
                {/* Current Status */}
                <div className="space-y-4">
                  <h3 className="text-base font-medium text-gray-900 border-b border-gray-200 pb-2">
                    Current Status
                  </h3>

                  <div className={`flex items-center justify-between p-4 rounded-lg border ${
                    isForwardingEnabled 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-gray-50 border-gray-200'
                  }`}>
                    <div className="flex items-center">
                      {isForwardingEnabled ? (
                        <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3" />
                      ) : (
                        <XCircleIcon className="h-6 w-6 text-gray-400 mr-3" />
                      )}
                      <div>
                        <p className={`font-medium ${isForwardingEnabled ? 'text-green-800' : 'text-gray-700'}`}>
                          {isForwardingEnabled ? 'Forwarding Enabled' : 'Forwarding Disabled'}
                        </p>
                        <p className="text-sm text-gray-600">
                          {selectedClient?.agent_name || selectedClient?.display_name || selectedClientId}
                          {selectedClient?.whatsapp_number && ` (${selectedClient.whatsapp_number})`}
                        </p>
                      </div>
                    </div>
                    {isForwardingEnabled && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Active
                      </span>
                    )}
                  </div>
                </div>

                {/* URL Configuration */}
                <div className="space-y-4">
                  <h3 className="text-base font-medium text-gray-900 border-b border-gray-200 pb-2">
                    Forward URL Configuration
                  </h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Webhook URL <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="url"
                        value={forwardUrl}
                        onChange={(e) => setForwardUrl(e.target.value)}
                        placeholder="https://your-api.com/webhook/whatsapp-messages"
                        className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        disabled={loadingUrl}
                      />
                    </div>
                    {loadingUrl ? (
                      <p className="text-sm text-gray-500 mt-2 flex items-center">
                        <ArrowPathIcon className="h-4 w-4 animate-spin mr-1" />
                        Loading current configuration...
                      </p>
                    ) : (
                      <p className="text-sm text-gray-500 mt-2">
                        Enter the URL where incoming messages will be forwarded via HTTP POST
                      </p>
                    )}
                    {hasChanges && (
                      <p className="text-sm text-amber-600 mt-1 flex items-center">
                        <span className="w-2 h-2 bg-amber-500 rounded-full mr-2"></span>
                        Unsaved changes
                      </p>
                    )}
                  </div>
                </div>

                {/* Payload Preview */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between border-b border-gray-200 pb-2">
                    <h3 className="text-base font-medium text-gray-900">
                      Sample Payload
                    </h3>
                    <button
                      type="button"
                      onClick={() => setShowPayloadPreview(!showPayloadPreview)}
                      className="text-sm text-green-600 hover:text-green-700 font-medium"
                    >
                      {showPayloadPreview ? 'Hide' : 'Show'} Preview
                    </button>
                  </div>

                  {showPayloadPreview && (
                    <div className="relative">
                      <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center text-gray-400 text-xs">
                            <CodeBracketIcon className="h-4 w-4 mr-1" />
                            JSON Payload
                          </div>
                          <button
                            type="button"
                            onClick={copyPayloadToClipboard}
                            className="flex items-center text-gray-400 hover:text-white text-xs transition-colors"
                          >
                            <ClipboardDocumentIcon className="h-4 w-4 mr-1" />
                            Copy
                          </button>
                        </div>
                        <pre className="text-sm text-green-400 font-mono whitespace-pre-wrap">
                          {JSON.stringify(samplePayload, null, 2)}
                        </pre>
                      </div>
                      <p className="text-sm text-gray-500 mt-2">
                        This is a sample of the JSON payload your endpoint will receive for each incoming message.
                      </p>
                    </div>
                  )}
                </div>

                {/* Info Box */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex">
                    <InformationCircleIcon className="h-5 w-5 text-amber-400 flex-shrink-0" />
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-amber-800">Important Notes</h4>
                      <ul className="mt-2 text-sm text-amber-700 list-disc list-inside space-y-1">
                        <li>All incoming messages will be forwarded, including group messages</li>
                        <li>Messages are forwarded before the bot decides whether to respond</li>
                        <li>Use HTTPS in production for secure data transmission</li>
                        <li>Your endpoint should respond within 30 seconds</li>
                        <li>The <code className="bg-amber-100 px-1 rounded">will_respond</code> field indicates if the bot will reply</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-between items-center pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleClear}
                    disabled={saving || loadingUrl || !originalUrl}
                    className="px-5 py-2.5 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Disable Forwarding
                  </button>

                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={() => setForwardUrl(originalUrl)}
                      disabled={saving || loadingUrl || !hasChanges}
                      className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Reset
                    </button>
                    <button
                      type="button"
                      onClick={handleSave}
                      disabled={saving || loadingUrl || !forwardUrl.trim() || !hasChanges}
                      className="px-8 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-green-500 to-green-600 rounded-lg hover:from-green-600 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-all transform hover:scale-[1.02] shadow-md"
                    >
                      {saving ? (
                        <>
                          <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        'Save Configuration'
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Empty State */}
            {!selectedClientId && (
              <div className="text-center py-12">
                <ArrowTopRightOnSquareIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-4 text-lg font-medium text-gray-900">No connection selected</h3>
                <p className="mt-2 text-sm text-gray-500">
                  Select a WhatsApp connection above to configure message forwarding.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfigureForwardUrlPage;
